import { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronRight } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { FloatingWhatsApp } from "@/components/landing/FloatingWhatsApp";
import { SectionHeading } from "@/components/landing/SectionHeading";
import { Icon3D } from "@/components/landing/Icon3D";
import { services } from "@/lib/servicesData";
import { WHATSAPP_URL } from "@/lib/constants";
import { usePageSEO, setJsonLd, SITE_URL } from "@/hooks/useCanonical";
import { fadeUp, heroFadeUp, cardReveal } from "@/lib/animations";

const useServiceSEO = (service) => {
  usePageSEO({
    path: `/solucoes/${service.slug}`,
    title: service.seoTitle,
    description: service.seoDesc,
  });

  useEffect(() => {
    const removeService = setJsonLd(`service-schema`, {
      "@context": "https://schema.org",
      "@type": "Service",
      name: service.name,
      description: service.seoDesc,
      serviceType: service.name,
      areaServed: { "@type": "Country", name: "Brasil" },
      provider: {
        "@type": "Organization",
        name: "Previsa Saúde",
        url: SITE_URL,
      },
      url: `${SITE_URL}/solucoes/${service.slug}`,
    });
    const removeBreadcrumb = setJsonLd(`breadcrumb-schema`, {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Início", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Soluções", item: `${SITE_URL}/#solucoes` },
        { "@type": "ListItem", position: 3, name: service.name },
      ],
    });
    return () => {
      removeService();
      removeBreadcrumb();
    };
  }, [service]);
};

const Breadcrumb = ({ name }) => (
  <nav aria-label="Trilha de navegação" data-testid="service-breadcrumb">
    <ol className="flex flex-wrap items-center gap-1.5 text-sm text-[#64748B]">
      <li>
        <a href="/" className="hover:text-[#3532B3] transition-colors">
          Início
        </a>
      </li>
      <li aria-hidden><ChevronRight size={14} /></li>
      <li>
        <a href="/#solucoes" className="hover:text-[#3532B3] transition-colors">
          Soluções
        </a>
      </li>
      <li aria-hidden><ChevronRight size={14} /></li>
      <li className="font-semibold text-[#1F2937]" aria-current="page">
        {name}
      </li>
    </ol>
  </nav>
);

const ServiceHero = ({ service }) => (
  <section
    id="inicio-servico"
    data-testid="service-hero"
    className="relative pt-32 md:pt-40 pb-12 overflow-hidden"
  >
    <div className="blob w-[480px] h-[480px] -top-32 -left-32 bg-[#3787F5]/30" />
    <div className="blob w-[420px] h-[420px] top-20 right-0 bg-[#12DDB5]/25" />

    <div className="relative max-w-7xl mx-auto px-6 md:px-10">
      <motion.div {...heroFadeUp} className="max-w-3xl">
        <Breadcrumb name={service.name} />
        <div className="mt-8 flex items-center gap-4">
          <Icon3D icon={service.icon} variant={service.variant} size="xl" />
        </div>
        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1F2937] tracking-[-0.025em] leading-[1.05]">
          <span className="text-gradient-brand">{service.h1}</span>
        </h1>
        <p className="mt-6 text-lg text-[#64748B] leading-relaxed">
          {service.intro}
        </p>
        <p className="mt-4 text-lg text-[#64748B] leading-relaxed">
          {service.intro2}
        </p>

        <div className="mt-9 flex flex-col sm:flex-row gap-4">
          <a
            data-testid="service-cta-whatsapp"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-gradient-to-r from-[#3532B3] to-[#3787F5] text-white font-semibold shadow-[0_12px_28px_rgba(53,50,179,0.28)] hover:shadow-[0_14px_36px_rgba(53,50,179,0.38)] hover:-translate-y-0.5 transition-all duration-300"
          >
            Fale com a Previsa
            <ArrowRight size={18} strokeWidth={2.2} />
          </a>
          <a
            data-testid="service-cta-all"
            href="/#solucoes"
            className="inline-flex items-center justify-center px-7 py-4 rounded-full bg-white text-[#1F2937] font-semibold border border-[#E5EAF2] hover:border-[#3787F5] hover:text-[#3532B3] hover:bg-[#F6F8FB] transition-all duration-300"
          >
            Ver todas as soluções
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

const StepsSection = ({ steps }) => (
  <section
    id="como-funciona"
    data-testid="service-steps"
    className="relative py-16 md:py-24 bg-[#F6F8FB]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <SectionHeading
        overline="Como funciona"
        title="Um fluxo técnico,"
        highlight="do atendimento ao parecer"
        testid="service-steps-heading"
      />
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {steps.map((s, idx) => (
          <motion.article
            key={s.title}
            {...cardReveal(idx * 0.06, 0.5)}
            data-testid={`service-step-${idx}`}
            className="group bg-white rounded-3xl border border-[#E5EAF2] p-7 hover:shadow-[0_20px_50px_rgba(53,50,179,0.10)] hover:border-[#3787F5]/30 hover:-translate-y-1 transition-all duration-300"
          >
            <div className="flex items-start justify-between">
              <Icon3D
                icon={s.icon}
                variant={s.variant}
                size="md"
                className="group-hover:scale-110 group-hover:-rotate-3"
              />
              <span className="w-7 h-7 rounded-full bg-[#12DDB5]/15 text-[#0b9a7c] text-xs font-extrabold flex items-center justify-center">
                {idx + 1}
              </span>
            </div>
            <h3 className="mt-5 text-lg font-bold text-[#1F2937]">{s.title}</h3>
            <p className="mt-2 text-[15px] text-[#64748B] leading-relaxed">
              {s.desc}
            </p>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);

const BenefitsSection = ({ benefits }) => (
  <section
    id="beneficios"
    data-testid="service-benefits"
    className="relative py-16 md:py-24"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <SectionHeading
        overline="Benefícios"
        title="O que a sua operação"
        highlight="ganha com isso"
        testid="service-benefits-heading"
      />
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {benefits.map((b, idx) => (
          <motion.article
            key={b.title}
            {...cardReveal(idx * 0.06, 0.5)}
            data-testid={`service-benefit-${idx}`}
            className="group bg-white rounded-3xl border border-[#E5EAF2] p-7 hover:shadow-[0_20px_50px_rgba(53,50,179,0.10)] hover:border-[#3787F5]/30 hover:-translate-y-1 transition-all duration-300"
          >
            <Icon3D
              icon={b.icon}
              variant={b.variant}
              size="md"
              className="mb-5 group-hover:scale-110 group-hover:-rotate-3"
            />
            <h3 className="text-lg font-bold text-[#1F2937]">{b.title}</h3>
            <p className="mt-2 text-[15px] text-[#64748B] leading-relaxed">
              {b.desc}
            </p>
          </motion.article>
        ))}
      </div>

      <motion.p
        {...fadeUp}
        className="mt-10 max-w-3xl text-lg text-[#64748B] leading-relaxed"
      >
        O serviço é voltado a <strong className="text-[#1F2937]">operadoras
        de planos de saúde e administradoras de benefícios</strong> de todo o
        Brasil. A operação é remota, com sede em Vila Velha/ES.
      </motion.p>
    </div>
  </section>
);

const RelatedServices = ({ related }) => (
  <section
    id="relacionados"
    data-testid="service-related"
    className="relative py-16 md:py-24 bg-[#F6F8FB]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <SectionHeading
        overline="Soluções relacionadas"
        title="Serviços que trabalham"
        highlight="em conjunto"
        testid="service-related-heading"
      />
      <div className="mt-12 grid sm:grid-cols-2 gap-6 max-w-4xl">
        {related.map((slug, idx) => {
          const r = services[slug];
          return (
            <motion.a
              key={slug}
              {...cardReveal(idx * 0.06, 0.5)}
              href={`/solucoes/${slug}`}
              data-testid={`related-service-${slug}`}
              className="group bg-white rounded-3xl border border-[#E5EAF2] p-8 hover:shadow-[0_24px_60px_rgba(53,50,179,0.10)] hover:border-[#3787F5]/30 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <Icon3D
                  icon={r.icon}
                  variant={r.variant}
                  size="lg"
                  className="group-hover:scale-110 group-hover:-rotate-3"
                />
                <ArrowUpRight
                  size={20}
                  className="text-[#CBD5E1] group-hover:text-[#3787F5] group-hover:rotate-12 transition-all duration-300"
                />
              </div>
              <h3 className="mt-6 text-xl font-bold text-[#1F2937]">
                {r.name}
              </h3>
              <p className="mt-2 text-[15px] text-[#64748B] leading-relaxed">
                {r.intro.slice(0, 140)}…
              </p>
            </motion.a>
          );
        })}
      </div>
    </div>
  </section>
);

export default function ServicePage() {
  const { slug } = useParams();
  const service = services[slug];

  if (!service) return <Navigate to="/" replace />;

  return <ServiceContent key={slug} service={service} />;
}

const ServiceContent = ({ service }) => {
  useServiceSEO(service);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [service]);

  return (
    <main data-testid="service-page" className="bg-white text-[#1F2937]">
      <Header />
      <ServiceHero service={service} />
      <StepsSection steps={service.steps} />
      <BenefitsSection benefits={service.benefits} />
      <RelatedServices related={service.related} />
      <Footer />
      <FloatingWhatsApp />
    </main>
  );
};
